import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, AlertTriangle, FileDown, ArrowRight } from "lucide-react";
import { LogoMark } from "@/components/Logo";

export default function DownloadPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState("idle"); // idle | busy | done | error
  const [error, setError] = useState("");
  const [orderInfo, setOrderInfo] = useState(null); // {valid, email, expires}
  const [checking, setChecking] = useState(!!token);

  useEffect(() => {
    document.title = "Download MITIVISE | TSM Group CLE";
  }, []);

  useEffect(() => {
    if (!token) return;
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/download/toolkit/info?token=${encodeURIComponent(token)}`);
        const data = await res.json();
        if (alive) {
          setOrderInfo(data);
          setChecking(false);
        }
      } catch (e) {
        if (alive) setChecking(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [token]);

  const invalid = orderInfo && !orderInfo.valid;

  const download = async () => {
    setState("busy");
    setError("");
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/download/toolkit?token=${encodeURIComponent(token)}`);
      if (!res.ok) {
        let detail = "This download link is not valid.";
        try {
          const parsed = await res.json();
          if (parsed?.detail) detail = parsed.detail;
        } catch {
          /* keep default */
        }
        throw new Error(detail);
      }
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement("a");
      a.href = url;
      a.download = "TSM_Compliance_OS_Toolkit.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setState("done");
    } catch (e) {
      setState("error");
      setError(e.message || "Download failed — please try again.");
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-paper" data-testid="download-page">
      <div className="noise-dark flex flex-1 flex-col items-center justify-center bg-navy px-6 py-16 text-center text-white">
        <LogoMark className="h-14 w-14" />
        <h1 className="mt-8 font-serif text-4xl font-semibold sm:text-5xl" data-testid="download-title">
          Your MITIVISE Toolkit
        </h1>
        {invalid ? (
          <p className="mt-8 inline-flex max-w-md items-start gap-2 rounded-sm border border-amber-400/30 bg-amber-400/10 px-4 py-3 text-left text-sm text-amber-200" data-testid="download-invalid">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            {orderInfo.reason === "expired"
              ? "This download link has expired. Contact TSM Group CLE to have a fresh link sent to you."
              : "This download link isn't valid. Please use the exact link from your order email, or contact TSM Group CLE for help."}
          </p>
        ) : (
          <>
            <p className="mt-4 max-w-lg text-white/60">
              One integrated Excel workbook, editable SOP templates (Word), and the Quick-Start guide (PDF) — all
              inside one download. The files are yours to keep forever.
            </p>
            {orderInfo?.valid && (
              <p className="mt-4 text-sm text-white/50" data-testid="download-order-info">
                Licensed to <strong className="text-white/80">{orderInfo.email}</strong>
                {orderInfo.expires ? ` · link expires ${new Date(orderInfo.expires).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}` : ""}
              </p>
            )}
            {token ? (
              <div className="mt-10 w-full max-w-md">
                <button
                  type="button"
                  onClick={download}
                  disabled={state === "busy" || checking}
                  data-testid="download-toolkit-button"
                  className="btn-gold w-full justify-center !py-4 text-base"
                >
                  {state === "busy" ? <Loader2 className="h-5 w-5 animate-spin" /> : <FileDown className="h-5 w-5" />}
                  {state === "busy" ? "Preparing your download…" : "Download the Toolkit (.zip)"}
                </button>
                {state === "done" && (
                  <p className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-emerald-400" data-testid="download-success">
                    <CheckCircle2 className="h-4 w-4" />
                    Downloaded — unzip the file and open the Excel workbook to begin.
                  </p>
                )}
                {state === "error" && (
                  <p className="mt-4 inline-flex items-start gap-2 rounded-sm border border-amber-400/30 bg-amber-400/10 px-4 py-3 text-left text-sm text-amber-200" data-testid="download-error">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    {error}
                  </p>
                )}
              </div>
            ) : (
              <p className="mt-10 rounded-sm border border-amber-400/30 bg-amber-400/10 px-4 py-3 text-sm text-amber-200" data-testid="download-missing-token">
                This page needs a secure download link from your order email.
              </p>
            )}
          </>
        )}
        <Link to="/mitivise" className="mt-12 inline-flex items-center gap-2 text-sm font-semibold text-white/60 transition-colors hover:text-gold" data-testid="download-back-link">
          Back to MITIVISE
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}
